import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { Button, Dialog, Notice } from "../components/ui";
import { ProjectForm } from "./ProjectForm";
import { emptyProjectSnapshot, projectLeader, projectValue } from "./project-detail";
import { planningYearLabel } from "./planning-period";
import { ProjectSection } from "./ProjectSection";

export function ProjectDetailDialog({ project, employees = [], user, onClose, onSaved, children, planningYear }) {
  const creating = !project.id;
  const [displayedProject, setDisplayedProject] = useState(project);
  const [mode, setMode] = useState(creating ? "edit" : "detail");
  const [action, setAction] = useState({ status: "idle", message: "" });
  const [menu, setMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editActions = useRef(null);
  const authorized = ["administrator", "manager", "consultant"].includes(user?.berechtigungsstufe);
  useEffect(() => { if (project.id) setDisplayedProject(project); }, [project]);
  const activeProject = displayedProject;
  const snapshot = activeProject.snapshot || emptyProjectSnapshot;
  const budget = Number(activeProject.budget) || 0;
  const budgetPercent = budget > 0 ? Math.min(100, (Number(snapshot.plannedHours) || 0) / budget * 100) : 0;
  const cancelEdit = () => {
    if (creating) onClose();
    else {
      setMode("detail");
      requestAnimationFrame(() => editActions.current?.querySelector("button")?.focus());
    }
  };
  const savedProject = (saved) => {
    setDisplayedProject(saved);
    setMode("detail");
    onSaved(saved);
  };
  const lifecycle = async (hard = false) => {
    setAction({ status: "saving", message: "" });
    try {
      const restored = activeProject.status === "archiviert";
      if (restored) await api(`/mandates/${activeProject.id}`, { method: "PATCH", body: JSON.stringify({ status: "aktiv", version: activeProject.version }) });
      else await api(`/mandates/${activeProject.id}?version=${activeProject.version}${hard ? "&hard=1" : ""}`, { method: "DELETE" });
      if (hard) return onClose();
      const confirmed = await api(`/mandates/${activeProject.id}`);
      onSaved(confirmed);
    } catch (error) {
      setAction({ status: error.status === 403 ? "forbidden" : error.status === 409 ? "conflict" : "error", message: error.message, hard });
    }
  };
  return (
    <Dialog title={mode === "edit" ? (creating ? "Neues Projekt" : "Projekt bearbeiten") : activeProject.name} onClose={mode === "edit" ? cancelEdit : onClose}>
      {mode === "edit" ? (
        <ProjectForm project={project} employees={employees} onCancel={cancelEdit} onSaved={savedProject} />
      ) : (
        <div className="project-dialog-layout project-readonly-detail">
          <ProjectSection title="Projektübersicht">
          <div className="project-title-actions">
            <div className="project-identity"><span><strong>Projekt-ID (Abacus)</strong> <span className="project-identity-value">{projectValue(activeProject.abacusNr)}</span> · {projectValue(activeProject.uid)}</span>{activeProject.gruppe && <span>Gruppe: {activeProject.gruppe}</span>}<span>Internes Projekt: {activeProject.intern ? "Ja" : "Nein"}</span></div>
            {authorized && <div ref={editActions} className="project-primary-actions"><Button onClick={() => setMode("edit")}>Bearbeiten</Button><Button className="project-delete-button" variant="secondary" onClick={() => setConfirmDelete(true)}>Löschen</Button></div>}
          </div>
          <div className="project-detail-summary">
            <p><span>Mandatsleitung</span><strong>{projectLeader(activeProject, employees)}</strong></p>
            <p><span>Phase(n)</span><strong>{snapshot.phases?.join(", ") || "–"}</strong></p>
            <p><span>Disposition</span><strong>{projectValue(snapshot.disposition)}</strong></p>
            <p><span>Budget</span><strong>{projectValue(activeProject.budget, " h")}</strong></p>
            <p><span>Geplant ({planningYearLabel(planningYear)})</span><strong>{projectValue(snapshot.plannedHours, " h")}</strong></p>
          </div>
          <div className="project-detail-budget" aria-label={`${snapshot.plannedHours || 0} von ${budget} Stunden geplant`}><span className={Number(snapshot.plannedHours) > budget ? "over" : ""} style={{ "--project-progress": `${budgetPercent}%` }} /></div>
          {action.status !== "idle" && action.status !== "saving" && <Notice tone="error"><strong>{action.status === "forbidden" ? "Keine Berechtigung." : action.status === "conflict" ? "Versionskonflikt." : "Aktion fehlgeschlagen."}</strong> {action.message}</Notice>}
          {authorized && <details className="project-extension-menu" open={menu} onToggle={(event) => setMenu(event.currentTarget.open)}><summary>Weitere Produktionsaktionen</summary><Button variant="secondary" disabled={action.status === "saving"} onClick={() => lifecycle(false)}>{activeProject.status === "archiviert" ? "Wiederherstellen" : "Archivieren"}</Button><Button variant="secondary" disabled={action.status === "saving"} onClick={() => setConfirmDelete(true)}>Endgültig löschen</Button></details>}
          </ProjectSection>
          {confirmDelete && <Dialog title="Projekt endgültig löschen" onClose={() => setConfirmDelete(false)}>
            <Notice tone="error"><strong>„{activeProject.name}“ endgültig löschen?</strong> Diese Aktion kann nicht rückgängig gemacht werden. {activeProject.offerteId && "Die ursprüngliche Offerte und ihre Follow-ups bleiben erhalten und können erneut in ein Projekt umgewandelt werden. "}{Number(snapshot.plannedHours) > 0 && "Das Projekt wird in der Planung verwendet und kann deshalb möglicherweise nur archiviert werden."}</Notice>
            {action.status === "conflict" && action.hard && <Notice tone="error">Das Projekt ist referenziert und kann nicht endgültig gelöscht werden. Archivieren Sie es stattdessen.</Notice>}
            <div className="form-actions"><Button disabled={action.status === "saving"} onClick={() => lifecycle(true)}>Endgültig löschen</Button><Button variant="secondary" onClick={() => setConfirmDelete(false)}>Abbrechen</Button>{action.status === "conflict" && action.hard && <Button variant="secondary" onClick={() => lifecycle(false)}>Stattdessen archivieren</Button>}</div>
          </Dialog>}
          {typeof children === "function" ? children(activeProject) : children}
        </div>
      )}
    </Dialog>
  );
}
